import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box,
  Autocomplete,
  TextField,
  FormControl,
  Grid,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Button,
  Typography,
} from '@mui/material'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import ClearIcon from '@mui/icons-material/Clear'
import DownloadForOfflineRoundedIcon from '@mui/icons-material/DownloadForOfflineRounded'
import TableTemplate, { Column } from '../../../../Common/Table/TableTemplate'
import { RootState, AppDispatch } from '../../../../../redux/store'
import {
  selectFilteredInvitedTeamMembers,
  selectInvitedTeamMembersLoading,
  selectInvitedTeamMembersError,
  selectEventStatusOptions,
  selectFunctionalAreaOptions,
  selectTidsPracticeOptions,
  selectSupervisorOptions,
  selectOperationalManagerOptions,
} from '../../../../../redux/reports/event/invitedTeamMemberPerEvent/selector'
import {
  setEventStatusFilter,
  setSearchQuery,
  setFunctionalAreaFilter,
  setSupervisorNameFilter,
  setTidsPracticeFilter,
  generateReport,
  setOperationalManagerNameFilter,
} from '../../../../../redux/reports/event/invitedTeamMemberPerEvent/slice'
import { fontSizes, fontWeights } from '../../../../Common/Typography/TypographyStyles'

const TeamMemberTable: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>()
  const [refreshTable, _setRefreshTable] = useState(false)

  const filteredReport = useSelector(selectFilteredInvitedTeamMembers)
  const loading = useSelector(selectInvitedTeamMembersLoading)
  const error = useSelector(selectInvitedTeamMembersError)
  const eventStatusOptions = useSelector(selectEventStatusOptions)
  const functionalAreaOptions = useSelector(selectFunctionalAreaOptions)
  const supervisorOptions = useSelector(selectSupervisorOptions)
  const operationalManagerOptions = useSelector(selectOperationalManagerOptions)
  const tidsPracticeOptions = useSelector(selectTidsPracticeOptions)

  const searchQuery = useSelector(
    (state: RootState) => state.invitedTeamMemberPerEvent.filters.searchQuery,
  )
  const functionalAreaFilter = useSelector(
    (state: RootState) => state.invitedTeamMemberPerEvent.filters.functionalArea,
  )
  const supervisorFilter = useSelector(
    (state: RootState) => state.invitedTeamMemberPerEvent.filters.supervisorName,
  )
  const operationalManagerFilter = useSelector(
    (state: RootState) => state.invitedTeamMemberPerEvent.filters.operationalManagerName,
  )
  const tidsPracticeFilter = useSelector(
    (state: RootState) => state.invitedTeamMemberPerEvent.filters.tidsPractice,
  )
  const eventStatusFilter = useSelector(
    (state: RootState) => state.invitedTeamMemberPerEvent.filters.eventStatus,
  )

  const handleSearchChange = (value: string) => {
    dispatch(setSearchQuery(value))
  }

  const handleClearSearch = () => {
    dispatch(setSearchQuery(''))
  }

  const handleEventStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setEventStatusFilter(event.target.value))
  }

  const handleFunctionalAreaChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setFunctionalAreaFilter(event.target.value))
  }

  const handleSupervisorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setSupervisorNameFilter(event.target.value))
  }

  const handleTidsPracticeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setTidsPracticeFilter(event.target.value))
  }

  const handleOperationalManagerFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setOperationalManagerNameFilter(event.target.value))
  }

  const handleGenerateReport = () => {
    dispatch(generateReport())
  }

  const columns: Column[] = [
    {
      field: 'fullName',
      headerName: 'TEAM MEMBER DETAILS',
      sortable: true,
      bodyAlignment: 'left',
      headerAlign: 'left',
      renderCell: (value: string, item: any) => (
        <>
          <Typography
            sx={{
              maxWidth: 200,
              whiteSpace: 'normal',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textAlign: 'left',
              display: 'block',
              margin: '0 auto',
              fontSize: fontSizes.size12,
              fontWeight: fontWeights[700],
            }}
          >
            {value}
          </Typography>
          <Typography
            sx={{
              maxWidth: 200,
              whiteSpace: 'normal',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textAlign: 'left',
              display: 'block',
              margin: '0 auto',
              fontSize: fontSizes.size12,
            }}
          >
            {item.email}
          </Typography>
        </>
      ),
    },
    {
      field: 'functionalArea',
      headerName: 'FUNCTIONAL AREA',
      sortable: true,
      bodyAlignment: 'left',
      headerAlign: 'left',
      renderCell: (value: string) => (
        <Typography
          sx={{
            maxWidth: 200,
            whiteSpace: 'normal',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textAlign: 'left',
            display: 'block',
            margin: '0 auto',
            fontSize: fontSizes.size12,
          }}
        >
          {value}
        </Typography>
      ),
    },
    {
      field: 'supervisorName',
      headerName: 'SUPERVISOR',
      sortable: true,
      bodyAlignment: 'left',
      headerAlign: 'left',
    },
    {
      field: 'operationalManagerName',
      headerName: 'OPERATIONAL MANAGER',
      sortable: true,
      bodyAlignment: 'left',
      headerAlign: 'left',
      renderCell: (value: string) => (
        <Typography
          sx={{
            maxWidth: 200,
            whiteSpace: 'normal',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textAlign: 'left',
            display: 'block',
            margin: '0 auto',
            fontSize: fontSizes.size12,
          }}
        >
          {value}
        </Typography>
      ),
    },

    {
      field: 'tidsPractice',
      headerName: 'TIDS PRACTICE',
      sortable: true,
      bodyAlignment: 'left',
      headerAlign: 'left',
      renderCell: (param: string) => (
        <div style={{ width: 200, whiteSpace: 'wrap' }}>
          <Box
            component='div'
            sx={{
              textOverflow: 'clip',
              overflow: 'hidden',
            }}
          >
            {param}
          </Box>
        </div>
      ),
    },
    {
      field: 'eventStatus',
      headerName: 'REGISTERED?',
      sortable: true,
      renderCell: (value: string) => (value.toLowerCase() === 'registered' ? 'Yes' : 'No'),
    },
  ]

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ m: 2 }}>
        {/* Search and Generate Report Grid */}
        <Grid container justifyContent={'flex-end'} alignItems='center' sx={{ mb: 2 }}>
          <Grid>
            <Button
              variant='contained'
              color='primary'
              startIcon={<DownloadForOfflineRoundedIcon />}
              onClick={handleGenerateReport}
            >
              Export CSV
            </Button>
          </Grid>
        </Grid>

        {/* Filters Grid */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl sx={{ width: '100%' }} variant='outlined' size='small'>
              <InputLabel htmlFor='search-input'>Search</InputLabel>
              <OutlinedInput
                id='search-input'
                value={searchQuery || ''}
                autoComplete='off'
                endAdornment={
                  <InputAdornment position='end'>
                    {searchQuery ? (
                      <IconButton
                        edge='end'
                        onClick={handleClearSearch}
                        aria-label='clear search'
                        id='search-clear-button'
                      >
                        <ClearIcon />
                      </IconButton>
                    ) : (
                      <SearchOutlinedIcon />
                    )}
                  </InputAdornment>
                }
                label='Search'
                placeholder='Search by Name or Email'
                type='text'
                onChange={(e) => handleSearchChange(e.target.value)}
              />
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 1.8 }}>
            <FormControl fullWidth>
              <Autocomplete
                options={['All', ...functionalAreaOptions]}
                value={functionalAreaFilter ? functionalAreaFilter : 'All'}
                renderInput={(params) => (
                  <TextField {...params} label='Functional Area' variant='outlined' size='small' />
                )}
                onChange={(_, value) =>
                  handleFunctionalAreaChange({ target: { value: value === 'All' ? '' : value } } as any)
                }
                size='small'
              />
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 1.8 }}>
            <FormControl fullWidth>
              <Autocomplete
                options={['All', ...(supervisorOptions || []).filter((option) => option !== null)]}
                value={supervisorFilter ? supervisorFilter : 'All'}
                renderInput={(params) => (
                  <TextField {...params} label='Supervisor' variant='outlined' size='small' />
                )}
                onChange={(_, value) =>
                  handleSupervisorChange({
                    target: { value: value === 'All' ? '' : value },
                  } as any)
                }
                size='small'
              />
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 1.8 }}>
            <FormControl fullWidth>
              <Autocomplete
                options={[
                  'All',
                  ...(operationalManagerOptions || []).filter((option) => option !== null),
                ]}
                value={operationalManagerFilter ? operationalManagerFilter : 'All'}
                renderInput={(params) => (
                  <TextField {...params} label='Operational Manager' variant='outlined' size='small' />
                )}
                onChange={(_, value) =>
                  handleOperationalManagerFilter({
                    target: { value: value === 'All' ? '' : value },
                  } as any)
                }
                size='small'
              />
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 1.8 }}>
            <FormControl fullWidth>
              <Autocomplete
                options={['All', ...tidsPracticeOptions]}
                value={tidsPracticeFilter ? tidsPracticeFilter : 'All'}
                renderInput={(params) => (
                  <TextField {...params} label='TIDS Practice' variant='outlined' size='small' />
                )}
                onChange={(_, value) =>
                  handleTidsPracticeChange({
                    target: { value: value === 'All' ? '' : value },
                  } as any)
                }
                size='small'
              />
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 1.8 }}>
            <FormControl fullWidth>
              <Autocomplete
                options={['All', ...eventStatusOptions]}
                value={eventStatusFilter ? eventStatusFilter : 'All'}
                renderInput={(params) => (
                  <TextField {...params} label='Registered?' variant='outlined' size='small' />
                )}
                onChange={(_, value) =>
                  handleEventStatusChange({ target: { value: value === 'All' ? '' : value } } as any)
                }
                size='small'
              />
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      <TableTemplate
        columns={columns}
        datas={filteredReport?.invitedTeamMembers || []}
        refreshTable={refreshTable}
        state={{ loading, error }}
        uniqueField='workdayId'
      />
    </Box>
  )
}

export default TeamMemberTable
